import { useMemo, useState, useEffect, useRef } from "react";
import { useGetShopsQuery } from "../../../api/shopsApi";
import { Vehicle } from "../../../api/vehiclesApi";
import { distanceService as defaultDistanceService } from "../../../services/distanceService";
import {
  formatDistance,
  mergeAerialAndDrivingDistances,
} from "../../../utils/shopUtils";
import {
  getVehicleLocation,
  VehicleLocation,
} from "../../../utils/vehicleUtils";
import { ShopWithDistance } from "../../../types/distance";
import { IDistanceService } from "../../../types/services";

interface UseClosestShopsResult {
  closestShops: ShopWithDistance[];
  isLoading: boolean;
  isCalculatingDriving: boolean;
  error: string | null;
}

export const useClosestShops = (
  selectedVehicle: Vehicle | undefined,
  limit: number = 20, // Get 20 shops by aerial distance
  distanceService: IDistanceService = defaultDistanceService // Dependency injection with default
): UseClosestShopsResult => {
  // Get trusted shops (preference = 0) and filter by isActive on frontend
  const { data: allShops = [], isLoading, error } = useGetShopsQuery(0);
  const activeShops = useMemo(
    () => allShops.filter((shop) => shop.isActive),
    [allShops]
  );

  const [drivingDistances, setDrivingDistances] = useState<ShopWithDistance[]>(
    []
  );
  const [isCalculatingDriving, setIsCalculatingDriving] = useState(false);

  // Refs to prevent excessive API calls
  const lastVehicleIdRef = useRef<number | null>(null);
  const calculationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCalculatingRef = useRef(false);
  const calculatedVehicleIdRef = useRef<number | null>(null);
  const initialVehicleLocationRef = useRef<VehicleLocation | null>(null);

  // Track vehicle location when selected (frozen at selection time to prevent SignalR recalculation)
  useEffect(() => {
    if (!selectedVehicle) {
      // Clear all state when no vehicle selected
      lastVehicleIdRef.current = null;
      initialVehicleLocationRef.current = null;
      calculatedVehicleIdRef.current = null;
      setDrivingDistances([]);
      return;
    }

    const isNewVehicle = lastVehicleIdRef.current !== selectedVehicle.id;
    const location = getVehicleLocation(selectedVehicle);

    // New vehicle selected
    if (isNewVehicle) {
      setDrivingDistances([]);
      calculatedVehicleIdRef.current = null;

      if (location) {
        // Location available immediately - store it
        initialVehicleLocationRef.current = location;
        lastVehicleIdRef.current = selectedVehicle.id;
      }
      // If location not available, we'll catch it on next render when SignalR updates
      return;
    }

    // Same vehicle - check if location just became available
    if (
      location &&
      initialVehicleLocationRef.current?.vehicleId !== selectedVehicle.id
    ) {
      initialVehicleLocationRef.current = location;
      lastVehicleIdRef.current = selectedVehicle.id;
    }
    // If same vehicle with location already stored, ignore SignalR updates (frozen location)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedVehicle?.id,
    selectedVehicle?.lastLatitude,
    selectedVehicle?.lastLongitude,
  ]);

  // Get location for current vehicle (returns null if not available yet)
  const getLocationForCurrentVehicle = (): VehicleLocation | null => {
    if (!selectedVehicle) return null;
    const location = initialVehicleLocationRef.current;
    return location?.vehicleId === selectedVehicle.id ? location : null;
  };

  // Calculate closest shops by aerial distance
  const closestShops = useMemo(() => {
    if (!selectedVehicle || !activeShops.length) {
      return [];
    }

    const location = getLocationForCurrentVehicle();
    if (!location) {
      // No location yet - prevents showing shops from previous vehicle
      return [];
    }

    // Get closest shops by aerial distance
    const closestShopsWithDistance =
      distanceService.getClosestShopsByAerialDistance(
        { latitude: location.latitude, longitude: location.longitude },
        activeShops,
        limit
      );

    // Format results
    return closestShopsWithDistance.map((shopWithDistance) => ({
      shop: shopWithDistance,
      aerialDistance: shopWithDistance.aerialDistance,
      distanceText: formatDistance(shopWithDistance.aerialDistance),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedVehicle?.id,
    initialVehicleLocationRef.current?.vehicleId,
    initialVehicleLocationRef.current?.latitude,
    initialVehicleLocationRef.current?.longitude,
    activeShops,
    limit,
    distanceService,
  ]);

  // Calculate driving distances for top 10 shops (only once per vehicle selection)
  const calculateDrivingDistances = async (
    location: VehicleLocation,
    shops: ShopWithDistance[]
  ): Promise<void> => {
    if (isCalculatingRef.current) return;

    isCalculatingRef.current = true;
    setIsCalculatingDriving(true);

    try {
      const top10Shops = shops.slice(0, 10).map((item) => item.shop);
      const drivingResults =
        await distanceService.calculateDrivingDistancesToShops(
          { latitude: location.latitude, longitude: location.longitude },
          top10Shops
        );

      const mergedResults = mergeAerialAndDrivingDistances(
        shops,
        drivingResults
      );
      setDrivingDistances(mergedResults);
      calculatedVehicleIdRef.current = location.vehicleId;
    } catch (error) {
      console.error("Error calculating driving distances:", error);
      // Fallback to aerial distances only
      setDrivingDistances(shops);
      calculatedVehicleIdRef.current = location.vehicleId;
    } finally {
      setIsCalculatingDriving(false);
      isCalculatingRef.current = false;
    }
  };

  // Trigger driving distance calculation when vehicle/location changes
  useEffect(() => {
    if (calculationTimeoutRef.current) {
      clearTimeout(calculationTimeoutRef.current);
    }

    if (!selectedVehicle) {
      setDrivingDistances([]);
      calculatedVehicleIdRef.current = null;
      return;
    }

    const location = getLocationForCurrentVehicle();
    if (!location) return; // Wait for location

    // Skip if already calculated for this vehicle (SignalR update)
    if (calculatedVehicleIdRef.current === selectedVehicle.id) return;

    // Skip if no shops to calculate for
    if (!closestShops.length) return;

    // Debounce calculation
    calculationTimeoutRef.current = setTimeout(() => {
      // Double-check vehicle hasn't changed
      const currentLocation = getLocationForCurrentVehicle();
      if (
        currentLocation?.vehicleId === selectedVehicle.id &&
        closestShops.length
      ) {
        calculateDrivingDistances(currentLocation, closestShops);
      }
    }, 500);

    return () => {
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedVehicle?.id,
    initialVehicleLocationRef.current?.vehicleId,
    initialVehicleLocationRef.current?.latitude,
    initialVehicleLocationRef.current?.longitude,
    closestShops.length,
    distanceService,
  ]);

  return {
    closestShops: drivingDistances.length > 0 ? drivingDistances : closestShops,
    isLoading,
    isCalculatingDriving,
    error: error ? "Failed to load shops" : null,
  };
};
